import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LOGOS, DISCORD_URL, listTeams, getBracket } from "../lib/api";
import { Trophy, Users, Shield, Swords, ArrowRight, ExternalLink } from "lucide-react";

const Stat = ({ label, value, icon: Icon }) => (
  <div className="glass-card rounded-xl p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase()}`}>
    <div className="h-12 w-12 rounded-lg bg-[#FF5E00]/15 flex items-center justify-center text-[#FF5E00]">
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <div className="font-display text-2xl font-black">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/60">{label}</div>
    </div>
  </div>
);

export default function Home() {
  const [teams, setTeams] = useState([]);
  const [bracket, setBracket] = useState([]);

  useEffect(() => {
    listTeams().then(setTeams).catch(() => {});
    getBracket().then(setBracket).catch(() => {});
  }, []);

  const live = bracket.length > 0;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top right, rgba(255,94,0,0.18), transparent 55%), radial-gradient(ellipse at bottom left, rgba(157,76,221,0.25), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5E00]/40 bg-[#FF5E00]/5 text-[#FF5E00] text-xs uppercase tracking-[0.25em] font-display mb-6">
              <span className="h-2 w-2 rounded-full bg-[#FF5E00] pulse-orange" />
              Season 2026 — Live
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl uppercase leading-[0.95] tracking-tight">
              The Apex
              <br />
              of <span className="text-gradient-orange">Gorilla Tag</span>
              <br />
              <span className="text-gradient-purple">Competition.</span>
            </h1>
            <p className="mt-6 text-white/70 text-lg max-w-xl leading-relaxed">
              EliteGorillaTag is the home of high-stakes 4v4 VR brawls. Climb the
              seedings, grind the bracket, and earn your spot in the top 16.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/bracket"
                className="font-display uppercase tracking-[0.18em] text-sm font-bold px-7 py-3.5 rounded-md bg-[#FF5E00] text-black hover:bg-[#ff7a33] transition-all transform hover:scale-105 neon-orange"
                data-testid="hero-cta-bracket"
              >
                View Bracket
              </Link>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-display uppercase tracking-[0.18em] text-sm font-bold px-7 py-3.5 rounded-md border border-[#9D4CDD] text-white hover:bg-[#9D4CDD]/15 transition-all inline-flex items-center gap-2"
                data-testid="hero-cta-discord"
              >
                Join Discord <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="relative flex items-center justify-center">
            <div className="absolute -inset-10 bg-[#FF5E00]/10 blur-3xl rounded-full" />
            <div className="absolute -inset-20 bg-[#9D4CDD]/15 blur-3xl rounded-full" />
            <img
              src={LOGOS.main}
              alt="EliteGorillaTag main logo"
              className="relative w-full max-w-md drop-shadow-[0_0_40px_rgba(255,94,0,0.35)]"
              data-testid="hero-logo"
            />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Teams" value={teams.length} icon={Users} />
          <Stat label="Bracket Slots" value={16} icon={Swords} />
          <Stat label="Status" value={live ? "Live" : "Seeding"} icon={Trophy} />
          <Stat label="Rules" value={11} icon={Shield} />
        </div>
      </section>

      {/* QUICK NAV */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              to: "/rules",
              title: "Rulebook",
              icon: Shield,
              desc: "11 chapters — from Discord conduct to in-game prohibited tactics.",
              color: "#FF5E00",
            },
            {
              to: "/teams",
              title: "Teams",
              icon: Users,
              desc: "Browse the rosters competing this season.",
              color: "#9D4CDD",
            },
            {
              to: "/standings",
              title: "Standings",
              icon: Trophy,
              desc: "Live seeding points across the league.",
              color: "#FF5E00",
            },
            {
              to: "/bracket",
              title: "Live Bracket",
              icon: Swords,
              desc: "Single-elimination 16-team championship bracket.",
              color: "#9D4CDD",
            },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="glass-card rounded-xl p-6 group hover:-translate-y-1 transition-all duration-300 hover:border-[#FF5E00]"
              data-testid={`quick-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="h-12 w-12 rounded-lg flex items-center justify-center mb-4"
                style={{ background: `${c.color}25`, color: c.color }}
              >
                <c.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display uppercase tracking-wider text-xl font-bold">
                {c.title}
              </h3>
              <p className="mt-2 text-white/65 text-sm">{c.desc}</p>
              <div className="mt-4 inline-flex items-center gap-2 text-[#FF5E00] text-sm font-display uppercase tracking-wider opacity-80 group-hover:opacity-100">
                Explore <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DISCORD BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div
          className="relative overflow-hidden rounded-2xl border border-[#9D4CDD]/40 p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-8"
          style={{
            background:
              "linear-gradient(135deg, rgba(88,101,242,0.18) 0%, rgba(157,76,221,0.18) 60%, rgba(255,94,0,0.18) 100%)",
          }}
        >
          <div className="absolute -right-12 -bottom-12 w-72 h-72 bg-[#5865F2]/30 blur-3xl rounded-full" />
          <div className="relative">
            <h2 className="font-display uppercase text-3xl lg:text-4xl font-black tracking-tight">
              Join the <span className="text-gradient-orange">Pack</span>.
            </h2>
            <p className="mt-3 text-white/75 max-w-xl">
              Sign up your team, find scrims, get match alerts, and connect with the
              entire community on Discord.
            </p>
          </div>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="relative font-display uppercase tracking-[0.18em] text-sm font-bold px-8 py-4 rounded-md bg-[#5865F2] text-white hover:bg-[#4752C4] transition-all inline-flex items-center gap-2"
            data-testid="banner-cta-discord"
          >
            discord.gg/EliteGorillaTag <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
